import { useState, useEffect } from 'react';
import { Mail, Send, Check, X, Loader2, ShieldCheck } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';

interface EmailVerificationProps {
  email: string;
  onEmailChange: (email: string) => void;
  onVerificationChange: (verified: boolean) => void;
  disabled?: boolean;
  label?: string;
  required?: boolean;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

export function EmailVerification({
  email,
  onEmailChange,
  onVerificationChange,
  disabled = false,
  label = 'Email',
  required = true
}: EmailVerificationProps) {
  const [isVerified, setIsVerified] = useState(false);
  const [showOtpField, setShowOtpField] = useState(false);
  const [otp, setOtp] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpStatus, setOtpStatus] = useState<'idle' | 'correct' | 'wrong'>('idle');
  const [countdown, setCountdown] = useState(0);
  const [otpSent, setOtpSent] = useState(false);

  // Countdown timer for resend OTP
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Reset verification when email changes
  useEffect(() => {
    setIsVerified(false);
    setShowOtpField(false);
    setOtp('');
    setOtpStatus('idle');
    setOtpSent(false);
    onVerificationChange(false);
  }, [email, onVerificationChange]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSendOtp = async () => {
    if (!validateEmail(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSendingOtp(true);
    try {
      await axios.post(`${API_URL}/email-verification/send-otp`, { email });
      toast.success('OTP sent to your email! Please check your inbox.');
      setShowOtpField(true);
      setOtpSent(true);
      setCountdown(60); // 60 seconds cooldown
    } catch (error: any) {
      console.error('Error sending OTP:', error);
      toast.error(error.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setIsVerifyingOtp(true);
    setOtpStatus('idle');

    try {
      const response = await axios.post(`${API_URL}/email-verification/verify-otp`, {
        email,
        otp
      });

      if (response.data.success) {
        setOtpStatus('correct');
        setIsVerified(true);
        onVerificationChange(true);
        toast.success('Email verified successfully!');
        
        // Hide OTP field after 2 seconds
        setTimeout(() => {
          setShowOtpField(false);
        }, 2000);
      }
    } catch (error: any) {
      console.error('Error verifying OTP:', error);
      setOtpStatus('wrong');
      toast.error(error.response?.data?.message || 'Invalid OTP. Please try again.');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleResendOtp = () => {
    setOtp('');
    setOtpStatus('idle');
    handleSendOtp();
  };

  return (
    <div className="space-y-3">
      <Label htmlFor="email">
        <Mail className="w-4 h-4 inline mr-2" />
        {label} {required && '*'}
      </Label>
      
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Input
            id="email"
            type="email"
            placeholder="your.email@company.com"
            value={email}
            onChange={(e) => {
              onEmailChange(e.target.value);
              if (isVerified) {
                setIsVerified(false);
                setShowOtpField(false);
                setOtpSent(false);
                onVerificationChange(false);
              }
            }}
            required={required}
            disabled={disabled || isVerified}
            className={`glass pr-10 ${isVerified ? 'border-green-500/50 bg-green-500/5' : ''}`}
          />
          {isVerified && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <ShieldCheck className="w-5 h-5 text-green-500" />
            </div>
          )}
        </div>
        
        {!isVerified && (
          <Button
            type="button"
            onClick={handleSendOtp}
            disabled={disabled || isSendingOtp || !email || countdown > 0}
            className="glass-strong border-primary/30 hover:border-primary/50 min-w-[100px]"
            variant="outline"
          >
            {isSendingOtp ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : countdown > 0 ? (
              `Wait ${countdown}s`
            ) : otpSent ? (
              'Resend'
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Verify
              </>
            )}
          </Button>
        )}
      </div>

      {/* OTP Input Field */}
      {showOtpField && !isVerified && (
        <div className="space-y-2 animate-in slide-in-from-top-2 duration-300">
          <Label htmlFor="otp" className="text-sm">
            Enter 6-digit OTP sent to your email
          </Label>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Input
                id="otp"
                type="text"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setOtp(value);
                  setOtpStatus('idle');
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && otp.length === 6) {
                    e.preventDefault();
                    handleVerifyOtp();
                  }
                }}
                disabled={disabled || isVerifyingOtp}
                className={`glass font-mono text-lg tracking-widest text-center pr-10 ${
                  otpStatus === 'correct'
                    ? 'border-green-500/50 bg-green-500/5'
                    : otpStatus === 'wrong'
                    ? 'border-red-500/50 bg-red-500/5'
                    : ''
                }`}
                maxLength={6}
              />
              {otpStatus !== 'idle' && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {otpStatus === 'correct' ? (
                    <Check className="w-5 h-5 text-green-500" />
                  ) : (
                    <X className="w-5 h-5 text-red-500" />
                  )}
                </div>
              )}
            </div>
            
            <Button
              type="button"
              onClick={handleVerifyOtp}
              disabled={disabled || isVerifyingOtp || otp.length !== 6}
              className="bg-primary hover:bg-primary/80 min-w-[100px]"
            >
              {isVerifyingOtp ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Verify
                </>
              )}
            </Button>
          </div>
          
          {countdown === 0 && otpSent && otpStatus !== 'correct' && (
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={isSendingOtp}
              className="text-sm text-primary hover:text-accent transition-colors underline"
            >
              Didn't receive OTP? Resend
            </button>
          )}
        </div>
      )}

      {/* Verification Status Messages */}
      {isVerified && (
        <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 bg-green-500/10 border border-green-500/20 rounded-lg p-3">
          <Check className="w-4 h-4" />
          <span>Email verified successfully</span>
        </div>
      )}
    </div>
  );
}
